import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Custom icon for driver
const DriverIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [30, 46],
  iconAnchor: [15, 46],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
  className: 'driver-marker'
});

interface LiveTrackingMapProps {
  pickupLocation: { lat: number; lng: number };
  dropoffLocation?: { lat: number; lng: number };
  driverLocation?: { lat: number; lng: number };
  showRoute?: boolean;
  height?: string;
  zoom?: number;
}

// Component to update map view when driver location changes
const MapUpdater: React.FC<{ driverLocation?: { lat: number; lng: number } }> = ({ driverLocation }) => {
  const map = useMap();

  useEffect(() => {
    if (driverLocation) {
      map.setView([driverLocation.lat, driverLocation.lng], map.getZoom());
    }
  }, [driverLocation, map]);

  return null;
};

const LiveTrackingMap: React.FC<LiveTrackingMapProps> = ({
  pickupLocation,
  dropoffLocation,
  driverLocation,
  showRoute = true,
  height = '400px',
  zoom = 13
}) => {
  const [route, setRoute] = useState<Array<[number, number]>>([]);

  // Calculate route if both locations are provided
  useEffect(() => {
    if (pickupLocation && dropoffLocation) {
      // Simple straight line route (for production, use a routing service like OSRM)
      setRoute([
        [pickupLocation.lat, pickupLocation.lng],
        [dropoffLocation.lat, dropoffLocation.lng]
      ]);
    }
  }, [pickupLocation, dropoffLocation]);

  // Determine center of map
  const getMapCenter = (): [number, number] => {
    if (driverLocation) {
      return [driverLocation.lat, driverLocation.lng];
    }
    if (dropoffLocation) {
      return [dropoffLocation.lat, dropoffLocation.lng];
    }
    return [pickupLocation.lat, pickupLocation.lng];
  };

  return (
    <div className="w-full rounded-lg overflow-hidden border border-gray-300" style={{ height }}>
      <MapContainer
        center={getMapCenter()}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Pickup location marker */}
        <Marker position={[pickupLocation.lat, pickupLocation.lng]}>
          <Popup>
            <div className="text-center">
              <p className="font-semibold">📍 Pickup Location</p>
            </div>
          </Popup>
        </Marker>

        {/* Dropoff location marker */}
        {dropoffLocation && (
          <Marker position={[dropoffLocation.lat, dropoffLocation.lng]}>
            <Popup>
              <div className="text-center">
                <p className="font-semibold">🎯 Drop-off Location</p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Driver location marker */}
        {driverLocation && (
          <Marker position={[driverLocation.lat, driverLocation.lng]} icon={DriverIcon}>
            <Popup>
              <div className="text-center">
                <p className="font-semibold">🚗 Driver Location</p>
                <p className="text-xs text-gray-500">Live tracking</p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Route line */}
        {showRoute && route.length > 0 && (
          <Polyline
            positions={route}
            color="#3b82f6"
            weight={4}
            opacity={0.7}
            dashArray="10, 10"
          />
        )}

        <MapUpdater driverLocation={driverLocation} />
      </MapContainer>
      <div className="bg-white p-2 border-t border-gray-300 text-xs text-gray-600">
        {driverLocation ? (
          <p>🚗 Driver is being tracked in real-time</p>
        ) : (
          <p>📍 Viewing errand locations</p>
        )}
      </div>
    </div>
  );
};

export default LiveTrackingMap;

